/**
 * TEMP-H-001: Execution API Client
 * HISI-FE-AUTH-01: Updated for cookie-based authentication
 *
 * Typed HTTP client for the four new backend endpoints.
 * Uses the live API base URL and cookie-based authentication.
 */

import type {
  ExecutionAPIClient,
  ExecutionSettings,
  StageThresholds,
  StageIterations,
  QCSummary,
  ExecutionHeartbeats
} from './api-types';
import { APIClientError, normalizeStage } from './api-types';
import type { StageType } from '@/types/execution';
import { API_BASE_URL } from './api-config';

class ExecutionAPI implements ExecutionAPIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // HISI-FE-AUTH-01: Cookie-based auth only
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`
        }));
        
        throw new APIClientError(
          error.message || `Request failed with status ${response.status}`,
          response.status,
          error.code,
          error.details
        ) as APIClientError;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof APIClientError) {
        throw error;
      }
      
      // Network or other errors
      throw new APIClientError(
        error instanceof Error ? error.message : 'Network request failed',
        0
      );
    }
  }

  async getSettings(executionId: string): Promise<ExecutionSettings> {
    return this.request<ExecutionSettings>(`/executions/${executionId}/settings`);
  }

  async getStageThresholds(executionId: string, stage: StageType): Promise<StageThresholds> {
    const normalizedStage = normalizeStage(stage);
    return this.request<StageThresholds>(`/executions/${executionId}/stages/${normalizedStage}/thresholds`);
  }

  async getStageIterations(executionId: string, stage: StageType): Promise<StageIterations> {
    const normalizedStage = normalizeStage(stage);
    return this.request<StageIterations>(`/executions/${executionId}/stages/${normalizedStage}/iterations`);
  }

  async getQCSummary(executionId: string, stage: StageType): Promise<QCSummary> {
    const normalizedStage = normalizeStage(stage);
    return this.request<QCSummary>(`/executions/${executionId}/stages/${normalizedStage}/qc-summary`);
  }

  async getHeartbeats(executionId: string): Promise<ExecutionHeartbeats> {
    return this.request<ExecutionHeartbeats>(`/executions/${executionId}/heartbeats`);
  }
}

// Export singleton instance
export const executionAPI = new ExecutionAPI();
export default executionAPI;